/**
 * Layout visual compartido para PDFs de marca (Purchase Order, Budget, …).
 * Paleta suave + cabecera con logo empresa + pie de página.
 */
import {
  formatEmpresaPdfAddressLines,
  getEmpresaPdfByKey,
} from "../constants/a3EmpresaPdf";

const DEFAULT_BRAND_RGB = [25, 118, 210];

export const normalizeBrandRgb = (raw) => {
  if (Array.isArray(raw) && raw.length >= 3) {
    return raw
      .slice(0, 3)
      .map((n) => Math.max(0, Math.min(255, Math.round(Number(n) || 0))));
  }
  return [...DEFAULT_BRAND_RGB];
};

export const mixRgb = (rgb, toward = 255, amount = 0.15) => {
  const [r, g, b] = normalizeBrandRgb(rgb);
  return [
    Math.round(r + (toward - r) * amount),
    Math.round(g + (toward - g) * amount),
    Math.round(b + (toward - b) * amount),
  ];
};

/** jsPDF exige r,g,b por separado — no acepta array en setFillColor/setDrawColor. */
export const setFillRgb = (doc, rgb) => {
  const [r, g, b] = rgb;
  doc.setFillColor(r, g, b);
};

export const setDrawRgb = (doc, rgb) => {
  const [r, g, b] = rgb;
  doc.setDrawColor(r, g, b);
};

export const setTextRgb = (doc, rgb) => {
  const [r, g, b] = rgb;
  doc.setTextColor(r, g, b);
};

/** Paleta suave para impresión (menos tinta, texto oscuro sobre fondos claros). */
export function createPrintPalette(brandRgb) {
  const brand = normalizeBrandRgb(brandRgb);
  return {
    brand,
    headerBand: mixRgb(brand, 255, 0.58),
    headerAccent: mixRgb(brand, 255, 0.42),
    headerText: mixRgb(brand, 0, 0.72),
    headerSubtext: mixRgb(brand, 0, 0.48),
    metaStrip: mixRgb(brand, 255, 0.38),
    metaStripText: mixRgb(brand, 0, 0.68),
    tableHead: mixRgb(brand, 255, 0.74),
    tableHeadText: mixRgb(brand, 0, 0.62),
    rowAlt: mixRgb(brand, 255, 0.94),
    rowFoot: mixRgb(brand, 255, 0.9),
    border: mixRgb(brand, 255, 0.55),
    accent: mixRgb(brand, 255, 0.35),
    totalBg: mixRgb(brand, 255, 0.93),
    totalText: mixRgb(brand, 0, 0.55),
  };
}

export const ensurePrintPalette = (palette, brandRgb) => {
  if (palette && Array.isArray(palette.border) && palette.border.length >= 3) {
    return palette;
  }
  return createPrintPalette(brandRgb);
};

export const loadPdfImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

export const drawSectionTitle = (doc, { x, y, title, rgb }) => {
  const [r, g, b] = rgb;
  doc.setFillColor(r, g, b);
  doc.rect(x, y - 3.8, 2.2, 5.2, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor(28, 32, 38);
  doc.text(title, x + 4.5, y);
};

function resolveFooterArgs(empOrOpts, paletteMaybe) {
  if (
    empOrOpts &&
    typeof empOrOpts === "object" &&
    ("emp" in empOrOpts || "palette" in empOrOpts)
  ) {
    return {
      emp: empOrOpts.emp,
      palette: empOrOpts.palette,
    };
  }
  return { emp: empOrOpts, palette: paletteMaybe };
}

export function drawPageFooter(
  doc,
  pageW,
  marginLeft,
  marginRight,
  pageH,
  pageNum,
  empOrOpts,
  paletteMaybe,
  pageLabel = "Page",
) {
  const { emp: empRaw, palette } = resolveFooterArgs(empOrOpts, paletteMaybe);
  const emp = empRaw || getEmpresaPdfByKey();
  const pal = ensurePrintPalette(palette, emp.headerRgb);
  const footerY = pageH - 12;
  setDrawRgb(doc, pal.border);
  doc.setLineWidth(0.2);
  doc.line(marginLeft, footerY - 3, pageW - marginRight, footerY - 3);
  doc.setFontSize(7.5);
  doc.setTextColor(110, 118, 128);
  const loc = [emp.zip, emp.city, emp.state].filter(Boolean).join(" ");
  const footerMid = [
    emp.name,
    emp.address ? `${emp.address}${loc ? `, ${loc}` : ""}` : loc,
    emp.web || emp.email || emp.vat,
  ]
    .filter(Boolean)
    .join("  ·  ");
  doc.text(footerMid, pageW / 2, footerY + 0.5, { align: "center" });
  doc.setTextColor(90, 98, 108);
  doc.text(`${pageLabel} ${pageNum}`, pageW - marginRight, footerY + 0.5, {
    align: "right",
  });
}

/**
 * Cabecera moderna: banda de marca + tarjeta de metadatos del documento.
 * @param {object} opts
 * @param {string} opts.docTitle — p.ej. "Purchase Order" | "Budget"
 * @param {string} opts.docRef — referencia visible en la tarjeta
 * @param {string} [opts.docDate] — fecha ya formateada (si no, hoy)
 */
export function drawPdfHeader(
  doc,
  {
    pageW,
    marginLeft,
    marginRight,
    logo,
    emp,
    docTitle,
    docRef,
    docDate,
    palette,
  },
) {
  const pal = ensurePrintPalette(palette, emp?.headerRgb);
  const addrLines = formatEmpresaPdfAddressLines(emp);
  const contactLines = [emp.phone, emp.email, emp.web].filter(Boolean);
  const lineStep = 4.2;
  const accentH = 5;
  const logoLayout = emp.logoLayout || "square";
  const leftLineCount = addrLines.length + contactLines.length;
  const contentBottom = 18.5 + leftLineCount * lineStep + 3;
  const headerH = Math.max(44, contentBottom + accentH + 4);

  setFillRgb(doc, pal.headerBand);
  doc.rect(0, 0, pageW, headerH, "F");
  setFillRgb(doc, pal.headerAccent);
  doc.rect(0, headerH - accentH, pageW, accentH, "F");

  let textX = marginLeft;
  if (logo && logoLayout !== "none") {
    doc.setFillColor(255, 255, 255);
    if (logoLayout === "wide") {
      const logoW = 38;
      const logoH = 11;
      const logoY = Math.max(8, (headerH - logoH) / 2);
      doc.roundedRect(marginLeft, logoY, logoW, logoH, 2, 2, "F");
      doc.addImage(
        logo,
        "PNG",
        marginLeft + 1.5,
        logoY + 0.6,
        logoW - 3,
        logoH - 1.2,
      );
      textX = marginLeft + logoW + 5;
    } else {
      const logoBox = 18;
      const logoY = Math.max(8, (headerH - logoBox) / 2);
      doc.roundedRect(marginLeft, logoY, logoBox, logoBox, 2.5, 2.5, "F");
      doc.addImage(
        logo,
        "PNG",
        marginLeft + 2,
        logoY + 2,
        logoBox - 4,
        logoBox - 4,
      );
      textX = marginLeft + logoBox + 5;
    }
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  setTextRgb(doc, pal.headerText);
  doc.text(emp.name, textX, 13.5);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.8);
  setTextRgb(doc, pal.headerSubtext);
  let ay = 18.5;
  for (const line of [...addrLines, ...contactLines]) {
    doc.text(line, textX, ay);
    ay += lineStep;
  }

  const metaW = 58;
  const metaH = 24;
  const metaX = pageW - marginRight - metaW;
  const metaY = 8;
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(metaX, metaY, metaW, metaH, 2.5, 2.5, "F");
  setFillRgb(doc, pal.metaStrip);
  doc.roundedRect(metaX, metaY, metaW, 5.5, 2.5, 2.5, "F");
  doc.rect(metaX, metaY + 3, metaW, 2.5, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  setTextRgb(doc, pal.metaStripText);
  doc.text(String(docTitle || ""), metaX + metaW / 2, metaY + 3.8, {
    align: "center",
  });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(28, 32, 38);
  const refLines = doc.splitTextToSize(String(docRef || "—"), metaW - 6);
  doc.text(refLines.slice(0, 2), metaX + metaW / 2, metaY + 12.5, {
    align: "center",
  });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(100, 108, 118);
  doc.text(
    docDate ||
      new Date().toLocaleDateString("es-ES"),
    metaX + metaW / 2,
    metaY + 18.5,
    { align: "center" },
  );

  return headerH + 8;
}

/** Bloque de parte (Supplier / Client): título + nombre + líneas de detalle. */
export function drawPartyBlock(
  doc,
  { marginLeft, contentW, y, title, name, detailLines = [], titleRgb },
) {
  drawSectionTitle(doc, {
    x: marginLeft,
    y: y + 0.5,
    title,
    rgb: titleRgb || [120, 128, 140],
  });
  y += 7;

  const displayName = String(name || "—").trim() || "—";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(28, 32, 38);
  const nameLines = doc.splitTextToSize(displayName, contentW);
  doc.text(nameLines, marginLeft, y);
  y += nameLines.length * 5.2 + 2;

  const lines = (detailLines || []).filter((ln) => String(ln || "").trim());
  if (lines.length) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(70, 76, 86);
    for (const line of lines) {
      const wrapped = doc.splitTextToSize(String(line), contentW);
      doc.text(wrapped, marginLeft, y);
      y += wrapped.length * 4.4 + 0.8;
    }
  }

  return y + 4;
}

/** Tarjeta de campos en 2 columnas + total destacado opcional. */
export function drawInfoCard(
  doc,
  {
    marginLeft,
    contentW,
    y,
    fields,
    palette,
    brandRgb,
    totalLabel = "Document total",
    totalSuffix = "",
  },
) {
  const pal = ensurePrintPalette(palette, brandRgb);
  const allFields = (fields || []).filter(Boolean);
  const totalField = allFields.find((p) => p.highlight);
  const gridFields = allFields.filter((p) => !p.highlight);
  const rows = Math.ceil(gridFields.length / 2);
  const cardH = 10 + rows * 8.5 + (totalField ? 10 : 0);

  doc.setFillColor(252, 253, 255);
  setDrawRgb(doc, pal.border);
  doc.setLineWidth(0.25);
  doc.roundedRect(marginLeft, y, contentW, cardH, 3, 3, "FD");

  setFillRgb(doc, pal.accent);
  doc.roundedRect(marginLeft, y, contentW, 2.2, 3, 3, "F");
  doc.rect(marginLeft, y + 1.5, contentW, 1.2, "F");

  const colW = contentW / 2 - 6;
  const drawField = (field, x, width, fieldY) => {
    if (!field) return;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.2);
    doc.setTextColor(110, 118, 128);
    doc.text(field.label, x, fieldY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.8);
    doc.setTextColor(38, 44, 52);
    const val = doc.splitTextToSize(String(field.value), width);
    doc.text(val, x, fieldY + 4.2);
  };
  let rowY = y + 9;
  for (let i = 0; i < gridFields.length; i += 2) {
    drawField(gridFields[i], marginLeft + 5, colW, rowY);
    drawField(gridFields[i + 1], marginLeft + contentW / 2 + 2, colW, rowY);
    rowY += 8.5;
  }

  if (totalField) {
    setFillRgb(doc, pal.totalBg);
    doc.roundedRect(marginLeft + 4, y + cardH - 9, contentW - 8, 7, 2, 2, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(80, 88, 98);
    doc.text(totalLabel, marginLeft + 7, y + cardH - 4.5);
    doc.setFontSize(10.5);
    setTextRgb(doc, pal.totalText);
    const suffix = totalSuffix ? ` ${totalSuffix}` : "";
    doc.text(
      `${totalField.value}${suffix}`,
      marginLeft + contentW - 7,
      y + cardH - 4.5,
      { align: "right" },
    );
  }

  return y + cardH + 8;
}

export function drawNotesBlock(doc, { marginLeft, contentW, y, notes, palette }) {
  const text = String(notes || "").trim();
  if (!text) return y;

  const pal = ensurePrintPalette(palette, palette?.brand);
  drawSectionTitle(doc, {
    x: marginLeft,
    y: y + 0.5,
    title: "Notes",
    rgb: pal.brand,
  });
  y += 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(70, 76, 86);
  const wrapped = doc.splitTextToSize(text, contentW);
  doc.text(wrapped, marginLeft, y);
  return y + wrapped.length * 4.4 + 6;
}
